/* eslint-disable react/jsx-props-no-spreading */
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuItemIndicator,
  DropdownMenuPortal,
  DropdownMenuTrigger,
} from '@radix-ui/react-dropdown-menu';
import {
  ComponentPropsWithoutRef,
  ElementRef,
  forwardRef,
  ReactNode,
  useState,
} from 'react';
import { twMerge } from 'tailwind-merge';
import Icon from '~/components/icon';
import { Simplify } from '~/utils/types';
import Field from './field';

const MultiSelectContent = forwardRef<
  ElementRef<typeof DropdownMenuContent>,
  ComponentPropsWithoutRef<typeof DropdownMenuContent>
>(({ className, children, sideOffset = 4, ...props }, ref) => (
  <DropdownMenuPortal>
    <DropdownMenuContent
      ref={ref}
      className={twMerge(
        'relative z-50 max-h-[--radix-dropdown-menu-content-available-height] w-[--radix-dropdown-menu-trigger-width] min-w-[8rem] max-w-[--radix-dropdown-menu-content-available-width] origin-[--radix-dropdown-menu-content-transform-origin] overflow-auto rounded-md border bg-white shadow-md',
        'data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:zoom-in-95',
        'data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95',
        'data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2',
        className,
      )}
      sideOffset={sideOffset}
      {...props}
    >
      <div className="p-1">{children}</div>
    </DropdownMenuContent>
  </DropdownMenuPortal>
));
MultiSelectContent.displayName = 'MultiSelectContent';

const MultiSelectItem = forwardRef<
  ElementRef<typeof DropdownMenuCheckboxItem>,
  Simplify<
    Omit<
      ComponentPropsWithoutRef<typeof DropdownMenuCheckboxItem> & {
        addon?: ReactNode;
        value: string;
        selected: boolean;
        onSelectChange: (select: boolean) => void;
      },
      'checked' | 'onCheckedChange'
    >
  >
>(
  (
    { className, children, addon, value, selected, onSelectChange, ...props },
    ref,
  ) => (
    <DropdownMenuCheckboxItem
      ref={ref}
      className={twMerge(
        'flex w-full cursor-default select-none items-center justify-between gap-2 rounded-sm px-2 py-1.5 text-sm outline-none',
        'focus:bg-gray-100 data-[disabled="true"]:pointer-events-none data-[disabled="true"]:opacity-50',
        className,
      )}
      onSelect={(e) => e.preventDefault()} // prevent closing the menu on select of an item
      checked={selected}
      onCheckedChange={onSelectChange}
      {...props}
    >
      {addon}
      <span>{children}</span>
      <span className="flex h-3.5 w-3.5 items-center justify-center">
        <DropdownMenuItemIndicator>
          <Icon icon="Check" className="size-4" />
        </DropdownMenuItemIndicator>
      </span>
    </DropdownMenuCheckboxItem>
  ),
);
MultiSelectItem.displayName = 'MultiSelectItem';

const MultiSelectTrigger = forwardRef<
  ElementRef<typeof DropdownMenuTrigger>,
  ComponentPropsWithoutRef<typeof DropdownMenuTrigger>
>(({ className, children, ...props }, ref) => (
  <DropdownMenuTrigger
    ref={ref}
    className={twMerge(
      // TODO: figure out how to have the trigger not collapse when there is no value
      // this is a hack to prevent the trigger from collapsing when there is no value
      'after:invisible after:content-["."]',
      'relative flex w-full items-center justify-between rounded-md border border-outline px-4 pb-1 pt-5 text-left [&>span]:line-clamp-1',
      'focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary',
      'data-[state="open"]:border-primary data-[state="open"]:outline-none data-[state="open"]:ring-1 data-[state="open"]:ring-primary',
      'disabled:cursor-not-allowed disabled:bg-gray-50 disabled:text-gray-500 disabled:ring-gray-200',
      className,
    )}
    {...props}
  >
    {children}
    <Icon
      icon="ChevronDown"
      className="absolute right-4 top-4 size-4 text-gray-500"
    />
  </DropdownMenuTrigger>
));
MultiSelectTrigger.displayName = 'MultiSelectTrigger';

// only valueMissing is applicable for select
type ValidationConstraint = Extract<keyof ValidityState, 'valueMissing'>;

type MultiSelectProps = {
  id: string;
  name: string;
  label: string;
  description?: ReactNode;
  validationMessages?: Record<ValidationConstraint, string>;
  required?: boolean;

  options: {
    label: ReactNode;
    /** MUST be unique across all options. */
    value: string;
  }[];
  value?: string[];
  defaultValue?: string[];
  onValueChange?: (value: string[]) => void;

  className?: string;
};

/**
 * Implementation inspired by: https://github.com/shadcn-ui/ui/discussions/859#discussioncomment-10788172
 */
function MultiSelect({
  id,
  name,
  label,
  description,
  validationMessages,
  required,

  options,
  value,
  defaultValue,
  onValueChange,

  className,
}: MultiSelectProps) {
  // need to control the select value to float the label
  const [fieldValue, setFieldValue] = useState<string[]>(
    value ?? defaultValue ?? [],
  );
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isValid, setIsValid] = useState(true);

  return (
    <Field.Root className={twMerge(className, 'relative')}>
      <DropdownMenu>
        <MultiSelectTrigger
          aria-required={required}
          aria-describedby={`${id}-error-text ${id}-help-text`}
          id={id}
          data-invalid={!isValid ? '' : undefined}
          data-empty={!fieldValue?.length ? '' : undefined}
          className={twMerge(
            'group',
            'data-[invalid]:border-error data-[invalid]:data-[state="open"]:border-error data-[invalid]:data-[state="open"]:ring-error data-[invalid]:focus:ring-error',
          )}
        >
          {!!fieldValue.length && fieldValue.join(', ')}

          <Field.Label
            htmlFor={id}
            className={twMerge(
              'pointer-events-none absolute left-0 ml-4 px-0 pb-1 pt-0 duration-100 ease-linear',
              // select has value
              '-translate-y-4 text-xs',
              // select is empty and opened
              'group-data-[empty]:group-data-[state="open"]:-translate-y-4 group-data-[empty]:group-data-[state="open"]:text-xs',
              // select is empty
              'group-data-[empty]:-translate-y-2 group-data-[empty]:pb-0 group-data-[empty]:text-sm group-data-[empty]:text-gray-500',
            )}
          >
            {label}
          </Field.Label>
        </MultiSelectTrigger>
        <MultiSelectContent>
          {options.map((option) => (
            <MultiSelectItem
              key={option.value}
              value={option.value}
              selected={fieldValue.includes(option.value)}
              onSelectChange={(selected) => {
                const updated =
                  selected ?
                    [...fieldValue, option.value]
                  : fieldValue.filter((item) => item !== option.value);
                setFieldValue(updated);
                setIsValid(true);
                setErrorMessage(null);
                onValueChange?.(updated);
              }}
            >
              {option.label}
            </MultiSelectItem>
          ))}
        </MultiSelectContent>
      </DropdownMenu>
      <input
        aria-hidden
        tabIndex={-1}
        required={required}
        name={name}
        value={fieldValue}
        onInvalid={() => {
          setIsValid(false);
          setErrorMessage(
            validationMessages?.valueMissing ??
              'Please select at least one item from the list.',
          );
        }}
        className="sr-only"
      />
      {errorMessage && (
        <Field.ErrorText id={`${id}-error-text`}>
          {errorMessage}
        </Field.ErrorText>
      )}
      {description && !errorMessage && (
        <Field.HelpText id={`${id}-help-text`}>{description}</Field.HelpText>
      )}
    </Field.Root>
  );
}

export { MultiSelect };
